<template>
  <section aria-label="Generated pump curves" class="pump-curves">
    <h3>Pump curves at {{ format(calculations.pumpHz.value) }} Hz</h3>
    <p>Calculated head–flow curves with BEP and section-local intake/discharge operating points. Flow is in-situ fluid volume, not surface production. Review each section of a mixed stack.</p>
    <p class="basis">Recalculated from this recommendation and the current preview inputs using the shared sizing chart calculations. These are model curves, not field measurements or a replay of the stored trace.</p>
    <article v-for="section in sections" :key="section.id">
      <h4>{{ section.name }} · {{ section.stages }} stages</h4>
      <template v-if="section.points.length && section.maxX > 0 && section.maxY > 0">
        <TornadoChart :minimum-height-px="350" :aspect-ratio="2.2" reverse-y draw-legend is-horizontal
          :x-unit="BarrelsPerDay" x-label="In-situ flow" y-label="Head (ft)"
          :frequency-data-sets="section.curves" :ror-data-sets="[]" :additional-data-sets="[]"
          :operating-points="section.markers" :title="section.name" />
        <ul class="legend"><li><span style="color: var(--plotColor0, #4899d8)">━</span> Calculated head curve</li><li v-for="marker in section.markers" :key="marker.label"><span :style="{ color: marker.color }">●</span> {{ marker.label }}: {{ format(marker.point.x) }} bbl/d</li></ul>
        <p v-if="!section.markers.some(marker => marker.label === 'BEP')">BEP data unavailable for this section.</p>
      </template>
      <p v-else role="status">Curve data unavailable. Review the pump catalog and calculation inputs.</p>
    </article>
    <p v-if="!sections.length" role="status">No selected pump sections are available.</p>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { useTaperedPumpTornadoChartingData } from "@/libs/charting/tornado/useTaperedPumpTornadoChartingData";
import { BarrelsPerDay } from "@/libs/units/flowRate";
import { Feet } from "@/libs/units/length";
import { HorsePower } from "@/libs/units/power";
import { PoundsPerSquareInchAbsolute } from "@/libs/units/pressure";
import TornadoChart from "@/components/scenarios/charts/TornadoChart.vue";

const props = defineProps<{ calculations: ScenarioCalculations }>();
const format = (value: number) => Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : "Unknown";
const valid = (p: { x: unknown; y: unknown }): p is { x: number; y: number } => typeof p.x === "number" && typeof p.y === "number" && Number.isFinite(p.x) && Number.isFinite(p.y) && p.x >= 0 && p.y >= 0;
const sections = computed(() => props.calculations.descendingOrderPumps.value.map(pump => {
  const hz = props.calculations.pumpHz.value;
  const data = Number.isFinite(hz) && hz > 0 ? useTaperedPumpTornadoChartingData(hz, hz, props.calculations, BarrelsPerDay, HorsePower, Feet, PoundsPerSquareInchAbsolute, pump) : undefined;
  const points = (data?.tdh.operatingDataSets[0]?.points || []).filter(valid);
  const bep = data?.tdh.rorDataSets.find(set => set.title === "BEP")?.points[0];
  const markers = (data?.tdh.operatingPointData.operatingPoints || []).filter(marker => valid(marker.point)).map(marker => ({ label: marker.label, point: marker.point, color: marker.label === "Intake" ? "#c77d16" : "#9775d1" }));
  if (data?.context.rorBep && bep && valid(bep)) markers.unshift({ label: "BEP", point: bep, color: "#2a9d74" });
  for (const title of ["ROR MIN", "ROR MAX"]) {
    const point = data?.tdh.rorDataSets.find(set => set.title === title)?.points[0];
    if (point && valid(point) && point.x > 0) markers.push({ label: title, point, color: "#8b929b" });
  }
  return { id: pump.id, name: pump.r_pump?.series_model || "Pump section", stages: pump.total_stages,
    points, markers, curves: (data?.tdh.operatingDataSets || []).map(curve => ({ ...curve, color: 'var(--plotColor0, #4899d8)' })), maxX: Math.max(0, ...points.map(p => p.x), ...markers.map(m => m.point.x)) * 1.05,
    maxY: Math.max(0, ...points.map(p => p.y), ...markers.map(m => m.point.y)) * 1.1 };
}));
</script>

<style scoped>
.pump-curves { margin: 1rem 0; }
article { border: 1px solid var(--surface-border); border-radius: 8px; padding: 1rem; margin-top: 1rem; }
svg { width: 100%; min-height: 220px; }
text { fill: var(--text-color); font-size: 12px; }
.legend { display: flex; flex-wrap: wrap; list-style: none; gap: 1rem; padding: 0; }
.basis { color: var(--text-color-secondary); font-size: 0.85rem; }
</style>
